import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizGapwordDropdown, {
  type QuizGapwordDropdownState,
} from "@/components/storyboard/quiz/QuizGapwordDropdown";
import {
  buildDropdownEntryReview,
  parseDropdownAnswers,
} from "@/components/storyboard/quiz/review/buildEntryReview";
import { useQuizValidations } from "@/hooks/useQuizValidations";
import { InteractiveElementStatusKind } from "@/lib/interactiveElementState";
import type { QuizDropdownEntryData } from "@/types/storyboard";

export interface QuizGapwordDropdownFlowProps {
  id: string;
  title: string;
  entries: QuizDropdownEntryData[];
  onValidate: (userAnswers: string[], entryIndex: number) => Promise<boolean>;
}

/** Cloze dropdown quiz in the shared interactive-element shell — inline in the
 * storyboard card. The verdict comes from the server, so a finished or
 * half-finished quiz is restored from `validation_data` on any device.
 */
const QuizGapwordDropdownFlow = ({
  id,
  title,
  entries,
  onValidate,
}: QuizGapwordDropdownFlowProps) => {
  const {
    status: statusOverride,
    isReady: validationsReady,
    refresh,
  } = useQuizValidations(
    id,
    entries.length,
    (answeredCount, correctCount): QuizGapwordDropdownState => ({
      currentIndex: answeredCount,
      userAnswers: [],
      isSubmitted: false,
      isCorrect: null,
      correctCount,
    }),
    (validation) =>
      buildDropdownEntryReview(
        entries[validation.entryIndex],
        parseDropdownAnswers(validation.userInput),
        !!validation.result.correct
      )
  );
  const serverResume =
    statusOverride?.kind === InteractiveElementStatusKind.InProgress
      ? (statusOverride.partial as QuizGapwordDropdownState)
      : undefined;

  return (
    <QuizInteractiveFlow<QuizGapwordDropdownState>
      id={id}
      title={title}
      illustrationKind="cloze_dropdown"
      layout="inline"
      statusOverride={statusOverride}
      reportProgress
      renderQuiz={({
        initialPartial,
        initialIndex,
        onPartialChange,
        onComplete,
      }) => {
        // The quiz seeds from useState on first mount: wait for the local
        // in-progress state or for validation_data to settle, so a cross-device
        // resume does not start at question 1.
        const seedReady = initialPartial != null || validationsReady;
        if (!seedReady) return null;

        return (
          <QuizGapwordDropdown
            entries={entries}
            onValidate={async (...args) => {
              const verdict = await onValidate(...args);
              refresh();
              return verdict;
            }}
            initialPartial={initialPartial ?? serverResume}
            initialIndex={initialIndex ?? serverResume?.currentIndex}
            onPartialChange={onPartialChange}
            onComplete={onComplete}
          />
        );
      }}
    />
  );
};

export default QuizGapwordDropdownFlow;
